import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  BusinessOutlined as BusinessIcon,
  DeleteOutline as DeleteIcon,
  ForumOutlined as ForumIcon,
  GroupsOutlined as GroupsIcon,
  InboxOutlined as InboxIcon,
  KeyOutlined as KeyIcon,
  ManageAccountsOutlined as ManageAccountsIcon,
  RefreshOutlined as RefreshIcon,
  SearchOutlined as SearchIcon,
  SendOutlined as SendIcon,
  ShieldOutlined as ShieldIcon
} from '@mui/icons-material';
import partnerQuoteService from '../services/partnerQuoteService';
import { useSocket } from '../context/SocketContext';
import { getPasswordPolicyError, MIN_PASSWORD_LENGTH, PASSWORD_POLICY_HELPER } from '../utils/passwordPolicy';
import {
  FileAttachField,
  FilePreviewDialog,
  MessageThread,
  QuoteItemsEditor,
  QuoteItemsSummary,
  STATUS_LABELS,
  STATUS_OPTIONS,
  calculateQuoteItemsTotal,
  hasValidQuoteItems,
  formatDate,
  formatMoney,
  useAsyncAction
} from './quoteExchangeShared';

const EMPTY_ACCOUNT_FORM = { company_name: '', contact_name: '', email: '', password: '' };
const EMPTY_OUTBOUND_QUOTE = { partner_id: '', subject: '', description: '', currency: 'USD', quoted_amount: '' };
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PartnerQuotesPage() {
  const theme = useTheme();
  const socket = useSocket();
  const realtimeTimerRef = useRef(null);
  const realtimeInFlightRef = useRef(false);
  const [tab, setTab] = useState(0);
  const [threads, setThreads] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [fileWarning, setFileWarning] = useState('');
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountForm, setAccountForm] = useState(EMPTY_ACCOUNT_FORM);
  const [outboundOpen, setOutboundOpen] = useState(false);
  const [outboundForm, setOutboundForm] = useState(EMPTY_OUTBOUND_QUOTE);
  const [outboundItems, setOutboundItems] = useState([]);
  const [outboundFiles, setOutboundFiles] = useState([]);
  const [filePreview, setFilePreview] = useState({ open: false, loading: false, error: '', data: null });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const accountPasswordError = getPasswordPolicyError(accountForm.password, {
    username: accountForm.contact_name,
    email: accountForm.email
  });

  const list = useAsyncAction();
  const accountsAction = useAsyncAction();
  const reply = useAsyncAction();
  const statusAction = useAsyncAction();
  const createAccountAction = useAsyncAction();
  const toggleAccountAction = useAsyncAction();
  const createThreadAction = useAsyncAction();
  const deleteThreadAction = useAsyncAction();

  // Single source of truth for loading the thread list. Previously the
  // mount effect (`initialize`) and the filter/search handler
  // (`loadThreads`) each re-implemented this, and only one of them kept
  // the current selection when the list refreshed — this version is used
  // in both places so behavior can't drift apart again.
  const loadThreads = async (preserveSelection = true) => {
    const data = await list.run(() => partnerQuoteService.listInternalThreads({ search, status }), {
      fallbackMessage: 'Unable to load partner quotations.'
    });
    const nextThreads = data.threads || [];
    setThreads(nextThreads);
    const keepId = preserveSelection && selectedId && nextThreads.some((thread) => thread.id === selectedId)
      ? selectedId
      : nextThreads[0]?.id;
    if (keepId) {
      await loadDetail(keepId);
    } else {
      setSelectedId(null);
      setDetail(null);
    }
  };

  const loadDetail = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const data = await partnerQuoteService.getInternalThread(id);
      setDetail(data);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadAccounts = () =>
    accountsAction.run(async () => {
      const data = await partnerQuoteService.listAccounts();
      setAccounts(data.accounts || []);
    }, { fallbackMessage: 'Unable to load partner accounts.' });

  const clearThreadFilters = async () => {
    setSearch('');
    setStatus('');
    const data = await list.run(() => partnerQuoteService.listInternalThreads({ search: '', status: '' }), {
      fallbackMessage: 'Unable to load partner quotations.'
    });
    const nextThreads = data.threads || [];
    setThreads(nextThreads);
    if (nextThreads[0]?.id) await loadDetail(nextThreads[0].id);
  };

  useEffect(() => {
    loadThreads(false);
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let disposed = false;
    const syncRealtimeState = async (event = {}) => {
      if (realtimeInFlightRef.current) return;
      realtimeInFlightRef.current = true;
      try {
        const data = await partnerQuoteService.listInternalThreads({ search, status });
        if (disposed) return;
        const nextThreads = data.threads || [];
        setThreads(nextThreads);
        const keepId = selectedId && nextThreads.some((thread) => thread.id === selectedId)
          ? selectedId
          : (event.threadId || nextThreads[0]?.id);
        if (!keepId) {
          setSelectedId(null);
          setDetail(null);
          return;
        }
        const nextDetail = await partnerQuoteService.getInternalThread(keepId);
        if (disposed) return;
        setSelectedId(keepId);
        setDetail(nextDetail);
      } catch (error) {
        // Keep the current view intact; explicit actions still surface API errors.
      } finally {
        realtimeInFlightRef.current = false;
      }
    };
    const handleRealtimeUpdate = (event = {}) => {
      window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => syncRealtimeState(event), 100);
    };
    const pollTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') syncRealtimeState();
    }, 3500);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncRealtimeState();
    };

    socket?.on('partner_quote:updated', handleRealtimeUpdate);
    socket?.on('connect', handleRealtimeUpdate);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      disposed = true;
      window.clearTimeout(realtimeTimerRef.current);
      window.clearInterval(pollTimer);
      socket?.off('partner_quote:updated', handleRealtimeUpdate);
      socket?.off('connect', handleRealtimeUpdate);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [socket, search, status, selectedId]);

  const sendReply = async () => {
    if (!selectedId || (!message.trim() && !files.length)) return;
    try {
      await reply.run(() => partnerQuoteService.addInternalMessage(selectedId, message, files), {
        fallbackMessage: 'Unable to send reply.'
      });
      setMessage('');
      setFiles([]);
      await loadThreads();
    } catch {
      // reply.error already shows the reason
    }
  };

  const updateStatus = async (nextStatus) => {
    if (!detail?.thread) return;
    try {
      await statusAction.run(() => partnerQuoteService.updateThread(detail.thread.id, {
        status: nextStatus,
        internal_owner_user_id: detail.thread.internal_owner_user_id
      }), { fallbackMessage: 'Unable to update status.' });
      await loadThreads();
    } catch {
      // statusAction.error already shows the reason
    }
  };

  const createAccount = async () => {
    try {
      await createAccountAction.run(() => partnerQuoteService.createAccount(accountForm), {
        fallbackMessage: 'Unable to create account.'
      });
      setAccountOpen(false);
      setAccountForm(EMPTY_ACCOUNT_FORM);
      await loadAccounts();
    } catch {
      // stays open, createAccountAction.error shows why
    }
  };

  const toggleAccount = async (account) => {
    try {
      await toggleAccountAction.run(
        () => partnerQuoteService.updateAccount(account.id, { is_active: !account.is_active }),
        { fallbackMessage: 'Unable to update account.' }
      );
      await loadAccounts();
    } catch {
      // toggleAccountAction.error shows why
    }
  };

  const createOutboundThread = async () => {
    try {
      const data = await createThreadAction.run(
        () => partnerQuoteService.createInternalThread(
          {
            ...outboundForm,
            quoted_amount: outboundForm.quoted_amount === '' ? null : Number(outboundForm.quoted_amount),
            items_json: JSON.stringify(outboundItems)
          },
          outboundFiles
        ),
        { fallbackMessage: 'Unable to send this quotation.' }
      );
      setOutboundOpen(false);
      setOutboundForm(EMPTY_OUTBOUND_QUOTE);
      setOutboundItems([]);
      setOutboundFiles([]);
      setTab(0);
      setSearch('');
      setStatus('');
      const threadData = await partnerQuoteService.getInternalThread(data.id);
      setSelectedId(data.id);
      setDetail(threadData);
      const listData = await partnerQuoteService.listInternalThreads();
      setThreads(listData.threads || []);
    } catch {
      // createThreadAction.error keeps the dialog actionable.
    }
  };

  const openFilePreview = async (file) => {
    if (!thread) return;
    setFilePreview({ open: true, loading: true, error: '', data: { name: file.original_name } });
    try {
      const data = await partnerQuoteService.getInternalFilePreview(thread.id, file);
      setFilePreview({ open: true, loading: false, error: '', data });
    } catch (error) {
      setFilePreview({ open: true, loading: false, error: error?.response?.data?.message || 'Unable to load this file.', data: null });
    }
  };

  const deleteThread = async () => {
    if (!thread) return;
    try {
      await deleteThreadAction.run(() => partnerQuoteService.deleteInternalThread(thread.id), {
        fallbackMessage: 'Unable to remove this quotation.'
      });
      setDeleteOpen(false);
      setSelectedId(null);
      setDetail(null);
      await loadThreads(false);
    } catch {
      // deleteThreadAction.error is shown in the confirmation dialog.
    }
  };

  const thread = detail?.thread;
  const messages = detail?.messages || [];
  const emailValid = accountForm.email === '' || EMAIL_PATTERN.test(accountForm.email);
  const combinedError = list.error || reply.error || statusAction.error || createAccountAction.error || toggleAccountAction.error || createThreadAction.error || fileWarning;

  return (
    <Box sx={{ width: '100%', pb: 3 }}>
      <Paper elevation={0} sx={{ mb: 1.5, p: { xs: 1.35, md: 1.6 }, borderRadius: 2.5, overflow: 'hidden', position: 'relative', color: '#fff', background: 'linear-gradient(118deg, #081b35 0%, #103d70 62%, #1d63b8 118%)', boxShadow: '0 10px 24px rgba(8,27,53,.18)' }}>
        <Box sx={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', right: -55, top: -100, border: '35px solid rgba(255,255,255,.05)' }} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#5ea6ff', color: '#081b35' }}><BusinessIcon sx={{ fontSize: 22 }} /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: '#9bc8ff', fontWeight: 900, letterSpacing: '.14em' }}>EXTERNAL SALES CHANNEL</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-.025em', overflowWrap: 'anywhere' }}>Partner Quotes</Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', .7), overflowWrap: 'anywhere' }}>Private quotation exchange with external sales partners.</Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadThreads(); loadAccounts(); }} sx={{ color: '#fff', borderColor: alpha('#fff', .4), width: { xs: '100%', sm: 'auto' } }}>Refresh</Button>
            <Button size="small" variant="contained" startIcon={<SendIcon />} onClick={() => setOutboundOpen(true)} disabled={!accounts.some((account) => account.is_active)} sx={{ bgcolor: '#fff', color: '#103d70', fontWeight: 900, width: { xs: '100%', sm: 'auto' }, '&:hover': { bgcolor: '#eaf3ff' } }}>Send quotation</Button>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAccountOpen(true)} sx={{ color: '#fff', borderColor: alpha('#fff', .4), fontWeight: 850, width: { xs: '100%', sm: 'auto' } }}>New partner</Button>
          </Stack>
        </Stack>
      </Paper>

      {combinedError && <Alert severity="error" onClose={() => setFileWarning('')} sx={{ mb: 2 }}>{combinedError}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ px: 1.3, minHeight: 44, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 44, py: .7 } }}
        >
          <Tab label={`Quote Inbox (${threads.length})`} />
          <Tab label={`Partner Access (${accounts.length})`} />
        </Tabs>

        {tab === 0 ? (
          <Box sx={{ p: 1.3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 1.3 }}>
              <TextField
                size="small" label="Search" value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && loadThreads()}
                InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
              <TextField size="small" select label="Status" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: { xs: 0, sm: 190 }, width: { xs: '100%', sm: 'auto' } }}>
                <MenuItem value="">All statuses</MenuItem>
                {STATUS_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
              <Button variant="outlined" onClick={() => loadThreads()}>Apply</Button>
            </Stack>

            {!list.pending && !threads.length ? (
              <Paper
                variant="outlined"
                sx={{
                  minHeight: 430,
                  borderRadius: 3,
                  overflow: 'hidden',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.15fr) minmax(330px, .85fr)' },
                  borderColor: alpha(theme.palette.primary.main, .2),
                  bgcolor: alpha(theme.palette.background.paper, .72)
                }}
              >
                <Box sx={{ p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ width: 58, height: 58, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, .14), color: 'primary.main' }}>
                    <InboxIcon sx={{ fontSize: 30 }} />
                  </Box>
                  <Typography variant="h5" sx={{ mt: 2.5, fontWeight: 900 }}>
                    {search || status ? 'No quotations match these filters' : 'Your partner inbox is ready'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 620, lineHeight: 1.7 }}>
                    {search || status
                      ? 'Adjust the search or status filter to return to the complete partner quotation inbox.'
                      : 'New quotations submitted by active partners will appear here automatically with their files, messages, amount, and workflow status.'}
                  </Typography>
                  {search || status ? (
                    <Button variant="outlined" onClick={clearThreadFilters} sx={{ mt: 3, alignSelf: 'flex-start' }}>
                      Clear filters
                    </Button>
                  ) : accounts.some((account) => account.is_active) ? (
                    <Button variant="contained" startIcon={<SendIcon />} onClick={() => setOutboundOpen(true)} sx={{ mt: 3, alignSelf: 'flex-start', fontWeight: 850 }}>
                      Send the first quotation
                    </Button>
                  ) : (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setTab(1); setAccountOpen(true); }} sx={{ mt: 3, alignSelf: 'flex-start', fontWeight: 850 }}>
                      Add a sales partner
                    </Button>
                  )}
                </Box>
                <Box sx={{ p: { xs: 3, md: 4 }, borderLeft: { lg: '1px solid' }, borderTop: { xs: '1px solid', lg: 0 }, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, .035), display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="overline" color="primary.main" sx={{ fontWeight: 900, letterSpacing: '.12em' }}>INTERNAL WORKFLOW</Typography>
                  <Stack spacing={2.5} sx={{ mt: 2.2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <ManageAccountsIcon color="primary" />
                      <Box><Typography fontWeight={850}>Controlled partner access</Typography><Typography variant="body2" color="text.secondary">Create or disable external accounts at any time.</Typography></Box>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <ForumIcon color="primary" />
                      <Box><Typography fontWeight={850}>One commercial thread</Typography><Typography variant="body2" color="text.secondary">Keep files, questions, and revisions together.</Typography></Box>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <ShieldIcon color="primary" />
                      <Box><Typography fontWeight={850}>Audited activity</Typography><Typography variant="body2" color="text.secondary">Every file and workflow action remains traceable.</Typography></Box>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '330px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
              <Box sx={{ minWidth: 0 }}>
                <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                  <List disablePadding sx={{ overflowY: 'auto', maxHeight: 650 }}>
                    {list.pending && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
                    {!list.pending && threads.map((item) => (
                      <ListItemButton
                        key={item.id} selected={selectedId === item.id} onClick={() => loadDetail(item.id)}
                        sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <ListItemText
                          primary={item.subject}
                          secondary={`${item.initiated_by === 'internal' ? 'Sent to' : 'Received from'} ${item.company_name} · ${STATUS_LABELS[item.status] || item.status}`}
                          primaryTypographyProps={{ fontWeight: 850, noWrap: true }}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    ))}
                    {!list.pending && !threads.length && (
                      <Box sx={{ p: 3 }}><Typography variant="body2" color="text.secondary">No quotations match this filter.</Typography></Box>
                    )}
                  </List>
                </Paper>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                {detailLoading ? (
                  <Paper variant="outlined" sx={{ minHeight: 520, borderRadius: 2.5, display: 'grid', placeItems: 'center' }}><CircularProgress /></Paper>
                ) : !thread ? (
                  <Paper variant="outlined" sx={{ minHeight: 520, borderRadius: 2.5, display: 'grid', placeItems: 'center' }}>
                    <Typography color="text.secondary">Select a quotation thread.</Typography>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, .045) }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" fontWeight={900} sx={{ overflowWrap: 'anywhere' }}>{thread.subject}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>{thread.reference_number} · {thread.company_name} · {thread.partner_email}</Typography>
                          <Chip size="small" variant="outlined" color={thread.initiated_by === 'internal' ? 'primary' : 'default'} label={thread.initiated_by === 'internal' ? 'Sent by SMTLINKS' : 'Received from partner'} sx={{ mt: 1, fontWeight: 800 }} />
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField
                            select size="small" label="Workflow status" value={thread.status}
                            onChange={(event) => updateStatus(event.target.value)}
                            disabled={statusAction.pending}
                            sx={{ minWidth: { xs: 0, sm: 210 }, width: { xs: '100%', sm: 'auto' } }}
                          >
                            {STATUS_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                          </TextField>
                          <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                            Remove
                          </Button>
                        </Stack>
                      </Stack>
                      {formatMoney(thread.quoted_amount, thread.currency) && (
                        <Chip size="small" sx={{ mt: 1 }} label={formatMoney(thread.quoted_amount, thread.currency)} />
                      )}
                    </Box>
                    <QuoteItemsSummary items={detail?.items || []} currency={thread.currency} />
                    <Divider />
                    <Stack spacing={1.5} sx={{ p: 2, minHeight: 330, maxHeight: 470, overflowY: 'auto', bgcolor: alpha(theme.palette.background.default, .5) }}>
                      <MessageThread messages={messages} viewerType="internal" onOpenFile={openFilePreview} />
                    </Stack>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <TextField fullWidth multiline minRows={3} label="Reply to partner" value={message} onChange={(event) => setMessage(event.target.value)} />
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mt: 1 }}>
                        <FileAttachField files={files} onChange={setFiles} onValidationError={(errors) => setFileWarning(errors.join(' '))} />
                        <Button variant="contained" endIcon={<SendIcon />} disabled={reply.pending || (!message.trim() && !files.length)} onClick={sendReply}>
                          {reply.pending ? <CircularProgress size={20} /> : 'Send reply'}
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Alert severity="info" icon={<ShieldIcon />} sx={{ mb: 2 }}>
              External accounts cannot access the internal dashboard. Deactivate access immediately when the commercial relationship ends.
            </Alert>
            {accountsAction.pending ? (
              <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                {accounts.map((account) => (
                  <Box key={account.id} sx={{ minWidth: 0 }}>
                    <Paper variant="outlined" sx={{ p: 2.2, height: '100%', borderRadius: 2.5, borderColor: account.is_active ? alpha(theme.palette.success.main, .3) : 'divider', bgcolor: account.is_active ? alpha(theme.palette.success.main, .025) : 'background.paper' }}>
                      <Stack direction="row" spacing={1.5} justifyContent="space-between">
                        <Stack direction="row" spacing={1.3} sx={{ minWidth: 0 }}>
                          <Box sx={{ width: 40, height: 40, flex: '0 0 auto', borderRadius: 2.2, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, .12), color: 'primary.main' }}>
                            <BusinessIcon fontSize="small" />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={900} noWrap>{account.company_name}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>{account.contact_name}</Typography>
                            <Typography variant="caption" color="primary.main" noWrap sx={{ display: 'block', mt: .2 }}>{account.email}</Typography>
                          </Box>
                        </Stack>
                        <Switch
                          checked={Boolean(account.is_active)}
                          onChange={() => toggleAccount(account)}
                          disabled={toggleAccountAction.pending}
                          inputProps={{ 'aria-label': `Toggle access for ${account.company_name}` }}
                        />
                      </Stack>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                        <Chip size="small" color={account.is_active ? 'success' : 'default'} label={account.is_active ? 'Active' : 'Disabled'} />
                        <Typography variant="caption" color="text.secondary">Last login: {formatDate(account.last_login_at)}</Typography>
                      </Stack>
                    </Paper>
                  </Box>
                ))}
                {!accounts.length && (
                  <Paper variant="outlined" sx={{ gridColumn: '1 / -1', minHeight: 320, p: 4, borderRadius: 3, display: 'grid', placeItems: 'center', textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, .025) }}>
                    <Box sx={{ maxWidth: 560 }}>
                      <Box sx={{ mx: 'auto', width: 58, height: 58, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, .14), color: 'primary.main' }}>
                        <GroupsIcon sx={{ fontSize: 30 }} />
                      </Box>
                      <Typography variant="h6" sx={{ mt: 2, fontWeight: 900 }}>No external partners yet</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
                        Create a restricted account for the sales partner. They will only see their own quotations, messages, and private files.
                      </Typography>
                      <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAccountOpen(true)} sx={{ mt: 2.5, fontWeight: 850 }}>
                        Create first partner
                      </Button>
                    </Box>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={deleteOpen} onClose={() => !deleteThreadAction.pending && setDeleteOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Remove quotation?</DialogTitle>
        <DialogContent dividers>
          <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 900 }}>{thread?.subject}</Box> will disappear from both active inboxes. Its audit record is retained for security and traceability.
          </Typography>
          {deleteThreadAction.error && <Alert severity="error" sx={{ mt: 2 }}>{deleteThreadAction.error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteThreadAction.pending}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={deleteThread} disabled={deleteThreadAction.pending}>
            {deleteThreadAction.pending ? <CircularProgress size={20} /> : 'Remove quotation'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={outboundOpen} onClose={() => !createThreadAction.pending && setOutboundOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>
          Send a quotation to a partner
          <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>Start a private commercial thread from SMTLINKS.</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.1} sx={{ pt: 1 }}>
            <TextField
              select label="Partner" required value={outboundForm.partner_id}
              onChange={(event) => setOutboundForm((prev) => ({ ...prev, partner_id: event.target.value }))}
            >
              {accounts.filter((account) => account.is_active).map((account) => (
                <MenuItem key={account.id} value={String(account.id)}>{account.company_name} · {account.contact_name}</MenuItem>
              ))}
            </TextField>
            <TextField label="Quotation subject" required value={outboundForm.subject} onChange={(event) => setOutboundForm((prev) => ({ ...prev, subject: event.target.value }))} placeholder="Parts, equipment, or commercial proposal" />
            <TextField multiline minRows={4} label="Message / scope" value={outboundForm.description} onChange={(event) => setOutboundForm((prev) => ({ ...prev, description: event.target.value }))} placeholder="Explain the requested parts, scope, lead time, or commercial notes." />
            <QuoteItemsEditor
              items={outboundItems}
              currency={outboundForm.currency}
              onChange={(nextItems) => {
                setOutboundItems(nextItems);
                const total = calculateQuoteItemsTotal(nextItems);
                if (total > 0) setOutboundForm((prev) => ({ ...prev, quoted_amount: total.toFixed(2) }));
              }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px minmax(0, 1fr)' }, gap: 2 }}>
              <TextField select label="Currency" value={outboundForm.currency} onChange={(event) => setOutboundForm((prev) => ({ ...prev, currency: event.target.value }))}>
                {['USD', 'CNY', 'MXN', 'EUR'].map((currency) => <MenuItem key={currency} value={currency}>{currency}</MenuItem>)}
              </TextField>
              <TextField type="number" label="Total quoted amount" value={outboundForm.quoted_amount} onChange={(event) => setOutboundForm((prev) => ({ ...prev, quoted_amount: event.target.value }))} inputProps={{ min: 0, step: '0.01' }} helperText={outboundItems.length ? 'Calculated automatically from quoted items; you may adjust it.' : ''} />
            </Box>
            <Box sx={{ p: 2, borderRadius: 2.5, border: '1px dashed', borderColor: 'primary.light', bgcolor: alpha(theme.palette.primary.main, .035) }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: .5, fontWeight: 800 }}>QUOTATION AND SUPPORTING FILES</Typography>
              <FileAttachField files={outboundFiles} onChange={setOutboundFiles} onValidationError={(errors) => setFileWarning(errors.join(' '))} />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOutboundOpen(false)} disabled={createThreadAction.pending}>Cancel</Button>
          <Button
            variant="contained" endIcon={<SendIcon />} onClick={createOutboundThread}
            disabled={createThreadAction.pending || !outboundForm.partner_id || !outboundForm.subject.trim() || (!outboundForm.description.trim() && !outboundFiles.length && !hasValidQuoteItems(outboundItems))}
          >
            {createThreadAction.pending ? <CircularProgress size={20} /> : 'Send quotation'}
          </Button>
        </DialogActions>
      </Dialog>

      <FilePreviewDialog
        open={filePreview.open}
        loading={filePreview.loading}
        error={filePreview.error}
        preview={filePreview.data}
        onClose={() => setFilePreview({ open: false, loading: false, error: '', data: null })}
        onDownload={() => partnerQuoteService.downloadPreview(filePreview.data)}
      />

      <Dialog open={accountOpen} onClose={() => !createAccountAction.pending && setAccountOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Create external partner access</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Company" required value={accountForm.company_name} onChange={(event) => setAccountForm((prev) => ({ ...prev, company_name: event.target.value }))} />
            <TextField label="Contact name" required value={accountForm.contact_name} onChange={(event) => setAccountForm((prev) => ({ ...prev, contact_name: event.target.value }))} />
            <TextField
              type="email" label="Email" required value={accountForm.email}
              error={!emailValid} helperText={!emailValid ? 'Enter a valid email address.' : ''}
              onChange={(event) => setAccountForm((prev) => ({ ...prev, email: event.target.value }))}
            />
            <TextField
              type="password" label="Temporary password" required value={accountForm.password}
              onChange={(event) => setAccountForm((prev) => ({ ...prev, password: event.target.value }))}
              error={Boolean(accountForm.password && accountPasswordError)}
              helperText={accountForm.password && accountPasswordError
                ? accountPasswordError
                : `${PASSWORD_POLICY_HELPER} Share it through a separate secure channel.`}
            />
            <Alert severity="warning">The system never displays this password again.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountOpen(false)} disabled={createAccountAction.pending}>Cancel</Button>
          <Button
            variant="contained" startIcon={<KeyIcon />} onClick={createAccount}
            disabled={
              createAccountAction.pending ||
              accountForm.password.length < MIN_PASSWORD_LENGTH ||
              Boolean(accountPasswordError) ||
              !emailValid ||
              !accountForm.company_name.trim() ||
              !accountForm.contact_name.trim() ||
              !accountForm.email.trim()
            }
          >
            {createAccountAction.pending ? <CircularProgress size={20} /> : 'Create access'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
